/**
 * Employee Action Plan Detail Component
 *
 * Shows detailed action plan for an individual employee including:
 * - Actions with priority levels
 * - Risk factors breakdown
 * - Communication preferences and best contact times
 * - Task creation and action tracking
 *
 * Based on MemberActionPlanEnhanced pattern from Member Retention Intelligence
 */

import React, { useState, useMemo } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Phone,
  MessageSquare,
  Mail,
  Home,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Flag,
  Plus,
  Brain,
  TrendingUp,
  TrendingDown,
  Loader2,
  ArrowLeft,
  User,
  Briefcase,
  DollarSign,
  Activity,
  Shield,
  Heart,
  Car,
  Baby,
  Building,
  X,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { config } from '@/src/config';

interface ContributingFactor {
  active: boolean;
  score: number;
  rent_burden_pct?: number;
  commute_minutes?: number;
  estimated_medical_debt?: number;
  ed_visits_12m?: number;
}

interface ActionPlanItem {
  category: string;
  action: string;
  urgency: string;
  impact: string;
}

interface EmployeeData {
  employee_id: number;
  name: string;
  department?: string;
  job_title?: string;
  tenure_months: number;
  hire_date?: string;
  churn_probability: number;
  engagement_score: number;
  sdoh_score: number;
  risk_level: string;
  risk_factor_count: number;
  priority_score: number;
  estimated_roi: number;
  contributing_factors: Record<string, ContributingFactor | boolean>;
  action_plan: ActionPlanItem[];
  recommended_interventions: Array<{
    type: string;
    action: string;
    impact: string;
    urgency: string;
  }>;
  // Contact info (if available)
  email?: string;
  phone?: string;
  // Communication preferences
  preferred_channel?: 'phone' | 'email' | 'sms';
  best_contact_day?: string;
  best_contact_time?: string;
}

interface ActionStep {
  id: number;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  action: string;
  method?: 'phone' | 'sms' | 'email' | 'home_visit' | 'task';
  timeframe: string;
  details: string;
  script?: string;
  completed?: boolean;
}

interface EmployeeActionPlanDetailProps {
  employee: EmployeeData;
  onBack: () => void;
  onActionComplete?: (stepId: number, employeeId: number) => void;
  onCreateTask?: (taskData: {
    title: string;
    description: string;
    priority: string;
    employee_id: number;
    due_date?: string;
  }) => void;
}

// Helper functions
const safeToFixed = (value: number | undefined | null, decimals: number = 0): string => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  return value.toFixed(decimals);
};

const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

// Priority badge variant
const getPriorityBadgeVariant = (priority: string): 'destructive' | 'default' | 'secondary' | 'outline' => {
  switch (priority) {
    case 'urgent':
      return 'destructive';
    case 'high':
      return 'default';
    case 'medium':
      return 'secondary';
    default:
      return 'outline';
  }
};

// Risk level colors
const getRiskColor = (riskLevel: string): string => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
      return 'text-red-600';
    case 'high':
      return 'text-orange-600';
    case 'elevated':
      return 'text-amber-600';
    case 'moderate':
      return 'text-yellow-600';
    case 'low':
    default:
      return 'text-green-600';
  }
};

// Risk badge variant
const getRiskBadgeVariant = (riskLevel: string): 'destructive' | 'default' | 'secondary' | 'outline' => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
      return 'destructive';
    case 'high':
      return 'default';
    case 'elevated':
    case 'moderate':
      return 'secondary';
    case 'low':
    default:
      return 'outline';
  }
};

// Progress bar color
const getRiskProgressColor = (riskLevel: string): string => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
      return 'bg-red-600';
    case 'high':
      return 'bg-orange-600';
    case 'elevated':
      return 'bg-amber-600';
    case 'moderate':
      return 'bg-yellow-600';
    case 'low':
    default:
      return 'bg-green-600';
  }
};

// Get icon for risk factor
const getRiskFactorIcon = (factorKey: string) => {
  switch (factorKey.toLowerCase()) {
    case 'housing':
    case 'housing_risk':
      return <Home className="h-4 w-4" />;
    case 'transportation':
    case 'transportation_risk':
      return <Car className="h-4 w-4" />;
    case 'childcare':
    case 'childcare_risk':
      return <Baby className="h-4 w-4" />;
    case 'financial':
    case 'financial_risk':
      return <DollarSign className="h-4 w-4" />;
    case 'medical':
    case 'medical_access':
    case 'medical_access_risk':
      return <Heart className="h-4 w-4" />;
    default:
      return <AlertTriangle className="h-4 w-4" />;
  }
};

// Generate action plan based on employee data
const generateActionPlan = (employee: EmployeeData): ActionStep[] => {
  const steps: ActionStep[] = [];
  let stepId = 1;

  const isUrgent = employee.risk_level === 'critical' || employee.churn_probability > 0.7;
  const isHighRisk = employee.risk_level === 'high' || employee.churn_probability > 0.4;
  const preferredChannel = employee.preferred_channel || 'email';

  // Step 1: Immediate action based on risk level
  if (isUrgent) {
    steps.push({
      id: stepId++,
      priority: 'urgent',
      action: 'Immediate intervention required',
      method: preferredChannel as any,
      timeframe: 'Within 24 hours',
      details: `${employee.name} is at critical risk with ${(employee.churn_probability * 100).toFixed(0)}% churn probability. Prioritize engagement today.`,
      script: `"Hi ${employee.name.split(' ')[0]}, this is [Your Name] from HR. I wanted to check in personally about your experience at [Company]. Do you have a few minutes to talk about any concerns or challenges you might be facing?"`,
    });
  } else if (isHighRisk) {
    steps.push({
      id: stepId++,
      priority: 'high',
      action: `Schedule one-on-one meeting with ${employee.name.split(' ')[0]}`,
      method: preferredChannel as any,
      timeframe: 'Within 48 hours',
      details: `High churn risk detected. Proactive engagement recommended.`,
      script: `"Hi ${employee.name.split(' ')[0]}, I'd like to schedule some time to discuss your career development and hear your feedback on your experience here."`,
    });
  }

  // Add actions based on contributing factors
  const factors = employee.contributing_factors || {};

  // Housing risk
  const housingFactor = factors['housing'] as ContributingFactor | undefined;
  if (housingFactor?.active && housingFactor.score >= 50) {
    steps.push({
      id: stepId++,
      priority: housingFactor.score >= 70 ? 'high' : 'medium',
      action: 'Connect with housing assistance resources',
      method: 'task',
      timeframe: 'Within 1 week',
      details: housingFactor.rent_burden_pct
        ? `Housing cost burden at ${housingFactor.rent_burden_pct.toFixed(0)}%. Employee may benefit from housing assistance programs or emergency funds.`
        : 'Housing risk detected. Explore available assistance programs.',
    });
  }

  // Transportation risk
  const transportFactor = factors['transportation'] as ContributingFactor | undefined;
  if (transportFactor?.active && transportFactor.score >= 50) {
    steps.push({
      id: stepId++,
      priority: transportFactor.score >= 70 ? 'high' : 'medium',
      action: 'Evaluate transportation solutions',
      method: 'task',
      timeframe: 'Within 1 week',
      details: transportFactor.commute_minutes
        ? `Commute time is ${transportFactor.commute_minutes} minutes. Consider remote work options, flexible scheduling, or transportation benefits.`
        : 'Transportation challenges identified. Review commuting support options.',
    });
  }

  // Childcare risk
  const childcareFactor = factors['childcare'] as ContributingFactor | undefined;
  if (childcareFactor?.active && childcareFactor.score >= 50) {
    steps.push({
      id: stepId++,
      priority: childcareFactor.score >= 70 ? 'high' : 'medium',
      action: 'Discuss childcare support options',
      method: 'phone',
      timeframe: 'Within 1 week',
      details: 'Childcare challenges may be affecting work-life balance. Discuss dependent care FSA, backup care benefits, or flexible scheduling.',
    });
  }

  // Financial risk
  const financialFactor = factors['financial'] as ContributingFactor | undefined;
  if (financialFactor?.active && financialFactor.score >= 50) {
    steps.push({
      id: stepId++,
      priority: financialFactor.score >= 70 ? 'high' : 'medium',
      action: 'Share financial wellness resources',
      method: 'email',
      timeframe: 'Within 1 week',
      details: financialFactor.estimated_medical_debt
        ? `Potential financial stress with estimated medical debt of ${formatCurrency(financialFactor.estimated_medical_debt)}. Share EAP financial counseling and payment plan options.`
        : 'Financial stress indicators present. Share financial wellness program information.',
    });
  }

  // Medical access risk
  const medicalFactor = factors['medical_access'] as ContributingFactor | undefined;
  if (medicalFactor?.active && medicalFactor.score >= 50) {
    steps.push({
      id: stepId++,
      priority: medicalFactor.score >= 70 ? 'high' : 'medium',
      action: 'Review healthcare utilization and access',
      method: 'task',
      timeframe: 'Within 2 weeks',
      details: medicalFactor.ed_visits_12m
        ? `${medicalFactor.ed_visits_12m} ED visits in past year. Help connect with PCP and preventive care services.`
        : 'Medical access barriers identified. Ensure awareness of telehealth and navigation services.',
    });
  }

  // Low engagement action
  if (employee.engagement_score < 40) {
    steps.push({
      id: stepId++,
      priority: 'medium',
      action: 'Conduct engagement survey or interview',
      method: 'task',
      timeframe: 'Within 2 weeks',
      details: `Engagement score is low (${safeToFixed(employee.engagement_score, 0)}). Gather feedback to understand barriers and satisfaction issues.`,
    });
  }

  // Add recommended interventions as action steps
  employee.recommended_interventions?.forEach((intervention) => {
    if (intervention.urgency === 'high') {
      steps.push({
        id: stepId++,
        priority: 'high',
        action: intervention.action,
        method: 'task',
        timeframe: 'Within 1 week',
        details: `${intervention.type}: ${intervention.impact}`,
      });
    }
  });

  // Follow-up step
  steps.push({
    id: stepId++,
    priority: 'low',
    action: 'Schedule follow-up check-in',
    method: 'task',
    timeframe: isUrgent ? '1 week' : '2 weeks',
    details: `Set reminder to follow up and track progress on interventions.`,
  });

  return steps;
};

export const EmployeeActionPlanDetail: React.FC<EmployeeActionPlanDetailProps> = ({
  employee,
  onBack,
  onActionComplete,
  onCreateTask,
}) => {
  const { t } = useAppTranslation();
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [showRiskDetails, setShowRiskDetails] = useState(true);
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<ActionStep | null>(null);
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [sendingCommunication, setSendingCommunication] = useState(false);

  const actionPlan = useMemo(() => generateActionPlan(employee), [employee]);

  const handleComplete = (stepId: number) => {
    setCompletedSteps((prev) => new Set([...prev, stepId]));
    onActionComplete?.(stepId, employee.employee_id);
  };

  const handleCreateTaskFromStep = (step: ActionStep) => {
    setSelectedStep(step);
    setTaskDescription(step.details);
    // Calculate due date based on timeframe
    const now = new Date();
    if (step.timeframe.includes('24 hours')) {
      now.setDate(now.getDate() + 1);
    } else if (step.timeframe.includes('48 hours')) {
      now.setDate(now.getDate() + 2);
    } else if (step.timeframe.includes('1 week')) {
      now.setDate(now.getDate() + 7);
    } else if (step.timeframe.includes('2 weeks')) {
      now.setDate(now.getDate() + 14);
    } else {
      now.setDate(now.getDate() + 7);
    }
    setTaskDueDate(now.toISOString().split('T')[0]);
    setCreateTaskDialogOpen(true);
  };

  const handleConfirmCreateTask = () => {
    if (selectedStep && onCreateTask) {
      onCreateTask({
        title: selectedStep.action,
        description: taskDescription,
        priority: selectedStep.priority,
        employee_id: employee.employee_id,
        due_date: taskDueDate,
      });
      handleComplete(selectedStep.id);
    }
    setCreateTaskDialogOpen(false);
    setSelectedStep(null);
    setTaskDescription('');
    setTaskDueDate('');
  };

  const urgentSteps = actionPlan.filter((s) => s.priority === 'urgent' || s.priority === 'high');
  const hasUrgentActions = urgentSteps.length > 0;
  const percentDisplay = Math.round((employee.churn_probability || 0) * 100);

  // Get active risk factors
  const activeRiskFactors = Object.entries(employee.contributing_factors || {})
    .filter(([_, value]) => {
      if (typeof value === 'boolean') return value;
      return (value as ContributingFactor)?.active;
    })
    .map(([key, value]) => ({
      key,
      factor: typeof value === 'object' ? (value as ContributingFactor) : null,
    }));

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('employer.employees.backToList')}
          </Button>
        </div>

        {/* Employee Summary Card */}
        <Card className={`border-l-4 ${
          employee.risk_level === 'critical' ? 'border-l-red-500' :
          employee.risk_level === 'high' ? 'border-l-orange-500' :
          employee.risk_level === 'elevated' ? 'border-l-amber-500' :
          employee.risk_level === 'moderate' ? 'border-l-yellow-500' :
          'border-l-green-500'
        }`}>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {employee.name}
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  {employee.job_title && (
                    <>
                      <Briefcase className="h-4 w-4" />
                      {employee.job_title}
                    </>
                  )}
                  {employee.department && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <Building className="h-4 w-4" />
                      {employee.department}
                    </>
                  )}
                  <span className="text-muted-foreground">·</span>
                  <Clock className="h-4 w-4" />
                  {t('employer.employees.monthsTenure', { months: employee.tenure_months })}
                </CardDescription>
              </div>
              <div className="text-right">
                <Badge variant={getRiskBadgeVariant(employee.risk_level)} className="mb-2">
                  {employee.risk_level.toUpperCase()} {t('employer.employees.risk')}
                </Badge>
                <div className="text-lg font-bold text-green-600">
                  {formatCurrency(employee.estimated_roi)}
                </div>
                <div className="text-xs text-muted-foreground">{t('employer.employees.estRoiFromIntervention')}</div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className="text-2xl font-bold">{safeToFixed(employee.sdoh_score, 0)}</div>
                <div className="text-xs text-muted-foreground">{t('employer.employees.sdohScore')}</div>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className="text-2xl font-bold">{safeToFixed(employee.engagement_score, 0)}</div>
                <div className="text-xs text-muted-foreground">{t('employer.employees.engagement')}</div>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className={`text-2xl font-bold ${getRiskColor(employee.risk_level)}`}>
                  {percentDisplay}%
                </div>
                <div className="text-xs text-muted-foreground">{t('employer.employees.churnRisk')}</div>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className="text-2xl font-bold">{employee.risk_factor_count}</div>
                <div className="text-xs text-muted-foreground">{t('employer.employees.riskFactors')}</div>
              </div>
            </div>

            {/* Churn Probability Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium">{t('employer.employees.churnProbability')}</p>
                <p className={`text-sm font-bold ${getRiskColor(employee.risk_level)}`}>
                  {percentDisplay}%
                </p>
              </div>
              <Progress
                value={percentDisplay}
                className="h-2"
                indicatorClassName={getRiskProgressColor(employee.risk_level)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Urgent Alert */}
        {hasUrgentActions && (
          <Alert className="border-orange-600 bg-orange-50">
            <Flag className="h-4 w-4 text-orange-600" />
            <AlertDescription>
              <div className="flex items-center justify-between w-full">
                <p className="text-sm font-medium">
                  {t('employer.employees.urgentActionsRequired', { count: urgentSteps.length })}
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Quick Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              {t('employer.employees.quickActions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              {employee.phone && (
                <Button size="sm" disabled={sendingCommunication}>
                  <Phone className="h-4 w-4 mr-2" />
                  {t('employer.employees.callEmployee')}
                </Button>
              )}
              {employee.phone && (
                <Button size="sm" variant="outline" disabled={sendingCommunication}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {t('employer.employees.sendSms')}
                </Button>
              )}
              {employee.email && (
                <Button size="sm" variant="outline" disabled={sendingCommunication}>
                  <Mail className="h-4 w-4 mr-2" />
                  {t('employer.employees.sendEmail')}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const defaultStep: ActionStep = {
                    id: 0,
                    priority: 'medium',
                    action: `Follow-up with ${employee.name}`,
                    method: 'task',
                    timeframe: '7 days',
                    details: `Custom follow-up task for ${employee.name}`,
                  };
                  handleCreateTaskFromStep(defaultStep);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('employer.employees.createTask')}
              </Button>
            </div>

            {/* Contact Preferences */}
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                {t('employer.employees.contactPreferences')}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-1.5">
                  {employee.preferred_channel === 'phone' ? (
                    <Phone className="h-4 w-4 text-muted-foreground" />
                  ) : employee.preferred_channel === 'sms' ? (
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Mail className="h-4 w-4 text-muted-foreground" />
                  )}
                  <p className="text-sm">
                    <strong>{t('employer.employees.preferred')}:</strong> {employee.preferred_channel || t('employer.employees.email')}
                  </p>
                </div>
                {employee.best_contact_day && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm">
                      <strong>{t('employer.employees.bestDay')}:</strong> {employee.best_contact_day}
                    </p>
                  </div>
                )}
                {employee.best_contact_time && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm">
                      <strong>{t('employer.employees.bestTime')}:</strong> {employee.best_contact_time}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Plan Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-600" />
              {t('employer.employees.recommendedActions')}
              <Badge variant="secondary" className="ml-2">
                {actionPlan.length}
              </Badge>
            </CardTitle>
            <CardDescription>
              {t('employer.employees.aiGeneratedActionPlan')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {actionPlan.map((step, index) => (
                <li
                  key={step.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                    completedSteps.has(step.id)
                      ? 'bg-green-50 border-green-200 opacity-70'
                      : 'bg-background border-border'
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div
                      className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        completedSteps.has(step.id)
                          ? 'bg-green-600'
                          : step.priority === 'urgent'
                          ? 'bg-red-600'
                          : step.priority === 'high'
                          ? 'bg-orange-600'
                          : step.priority === 'medium'
                          ? 'bg-blue-600'
                          : 'bg-gray-500'
                      }`}
                    >
                      {completedSteps.has(step.id) ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        index + 1
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p
                        className={`text-sm ${
                          step.priority === 'urgent' || step.priority === 'high'
                            ? 'font-bold'
                            : 'font-medium'
                        } ${completedSteps.has(step.id) ? 'line-through' : ''}`}
                      >
                        {step.action}
                      </p>
                      <Badge
                        variant={getPriorityBadgeVariant(step.priority)}
                        className="h-5 text-[10px] px-1.5"
                      >
                        {step.priority.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {step.timeframe}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {step.details}
                    </p>
                    {step.script && (
                      <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800 italic">
                        <strong>{t('employer.employees.suggestedScript')}:</strong> {step.script}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleCreateTaskFromStep(step)}
                          disabled={completedSteps.has(step.id)}
                        >
                          <ClipboardList className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('employer.employees.createTask')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleComplete(step.id)}
                          disabled={completedSteps.has(step.id)}
                        >
                          <CheckCircle
                            className={`h-4 w-4 ${
                              completedSteps.has(step.id)
                                ? 'text-green-600'
                                : 'text-muted-foreground'
                            }`}
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('employer.employees.markComplete')}</TooltipContent>
                    </Tooltip>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Risk Factors Card */}
        <Card>
          <CardHeader>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowRiskDetails(!showRiskDetails)}
            >
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-600" />
                {t('employer.employees.riskFactorAnalysis')}
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {showRiskDetails ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          {showRiskDetails && (
            <CardContent>
              {activeRiskFactors.length > 0 ? (
                <div className="space-y-3">
                  {activeRiskFactors.map(({ key, factor }) => (
                    <div
                      key={key}
                      className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200"
                    >
                      <div className="flex-shrink-0 text-red-600">
                        {getRiskFactorIcon(key)}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium capitalize">
                          {key.replace(/_/g, ' ')}
                        </p>
                        {factor && (
                          <div className="flex items-center gap-4 mt-1">
                            <p className="text-xs text-muted-foreground">
                              {t('employer.employees.score')}: <strong>{safeToFixed(factor.score, 0)}</strong>
                            </p>
                            {factor.rent_burden_pct && (
                              <p className="text-xs text-muted-foreground">
                                {t('employer.employees.rentBurden')}: <strong>{safeToFixed(factor.rent_burden_pct, 0)}%</strong>
                              </p>
                            )}
                            {factor.commute_minutes && (
                              <p className="text-xs text-muted-foreground">
                                {t('employer.employees.commute')}: <strong>{factor.commute_minutes} {t('employer.common.minutes')}</strong>
                              </p>
                            )}
                            {factor.ed_visits_12m && (
                              <p className="text-xs text-muted-foreground">
                                {t('employer.employees.edVisits')}: <strong>{factor.ed_visits_12m}</strong>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        {factor && (
                          <Badge
                            variant={factor.score >= 70 ? 'destructive' : factor.score >= 50 ? 'default' : 'secondary'}
                          >
                            {factor.score >= 70 ? 'HIGH' : factor.score >= 50 ? 'ELEVATED' : 'MODERATE'}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  {t('employer.employees.noActiveRiskFactors')}
                </p>
              )}
            </CardContent>
          )}
        </Card>

        {/* Recommended Resources */}
        {employee.recommended_interventions && employee.recommended_interventions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-600" />
                {t('employer.employees.recommendedResources')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {employee.recommended_interventions.map((intervention, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200"
                  >
                    <div
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        intervention.urgency === 'high'
                          ? 'bg-red-500'
                          : intervention.urgency === 'medium'
                          ? 'bg-orange-500'
                          : 'bg-green-500'
                      }`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-green-800 uppercase">
                          {intervention.type}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {intervention.urgency}
                        </Badge>
                      </div>
                      <p className="text-sm">{intervention.action}</p>
                      <p className="text-xs text-muted-foreground">{intervention.impact}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={createTaskDialogOpen} onOpenChange={setCreateTaskDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              {t('employer.employees.createTaskFromAction')}
            </DialogTitle>
          </DialogHeader>
          {selectedStep && (
            <div className="pt-4 space-y-4">
              <div>
                <h4 className="text-sm font-semibold mb-2">{selectedStep.action}</h4>
                <Badge variant={getPriorityBadgeVariant(selectedStep.priority)}>
                  {selectedStep.priority.toUpperCase()}
                </Badge>
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-description">{t('employer.employees.taskDescription')}</Label>
                <textarea
                  id="task-description"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-due-date">{t('employer.employees.dueDate')}</Label>
                <Input
                  id="task-due-date"
                  type="date"
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTaskDialogOpen(false)}>
              {t('employer.common.cancel')}
            </Button>
            <Button onClick={handleConfirmCreateTask}>
              <Plus className="h-4 w-4 mr-2" />
              {t('employer.employees.createTask')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default EmployeeActionPlanDetail;
